<template>
  <v-dialog v-model="dialog" max-width="560" persistent>
    <v-card>
      <v-card-title class="d-flex align-center ga-2">
        <v-icon size="20">mdi-account-arrow-right</v-icon>
        {{ $t("ncrm.integrations.telegram.invite.title") }}
      </v-card-title>
      <v-divider />

      <v-card-text class="pt-4">
        <v-progress-circular v-if="loading" indeterminate size="22" />

        <template v-else>
          <v-alert v-if="noBots" type="warning" density="compact" class="mb-3">
            {{ $t("ncrm.integrations.telegram.invite.noBots") }}
          </v-alert>

          <!-- Already linked -->
          <template v-else-if="existingLink">
            <div class="d-flex align-center ga-3">
              <v-icon color="success">mdi-check-circle</v-icon>
              <div>
                <div class="font-weight-medium">
                  {{
                    $t("ncrm.integrations.telegram.invite.alreadyLinked", {
                      bot: selectedBotUsername ? "@" + selectedBotUsername : "bot",
                    })
                  }}
                </div>
                <div v-if="existingLink.telegram_username" class="text-caption text-medium-emphasis">
                  {{ $t("ncrm.integrations.telegram.invite.linkedAs") }} @{{ existingLink.telegram_username }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  {{ $t("ncrm.integrations.telegram.invite.linkedAt") }} {{ formatDate(existingLink.linked_at) }}
                </div>
              </div>
              <v-spacer />
              <v-btn size="small" variant="text" color="error" :loading="unlinking" @click="unlink">
                {{ $t("ncrm.integrations.telegram.invite.unlink") }}
              </v-btn>
            </div>
          </template>

          <!-- Bot picker (rare — usually 1 bot) -->
          <v-select
            v-if="!existingLink && activeBots.length > 1"
            v-model="selectedAccountId"
            :items="botOptions"
            :label="$t('ncrm.integrations.telegram.invite.pickBot')"
            hide-details
            class="mb-3"
            @update:model-value="refresh"
          />

          <!-- Active code panel -->
          <div v-if="!existingLink && activeCode" class="telegram-invite-code-card">
            <div class="text-caption text-medium-emphasis mb-1">
              {{ $t("ncrm.integrations.telegram.invite.codeReady") }}
            </div>
            <div class="d-flex align-center ga-3 flex-wrap mb-2">
              <code class="telegram-invite-code">{{ activeCode.code }}</code>
              <v-chip size="x-small" color="warning">
                {{ $t("ncrm.integrations.telegram.invite.expiresIn", { time: countdownLabel }) }}
              </v-chip>
            </div>

            <div class="telegram-invite-deeplink mb-2">
              <span class="text-caption text-medium-emphasis mr-1">Link:</span>
              <code class="telegram-invite-deeplink-url">{{ deepLink }}</code>
            </div>

            <div class="d-flex flex-wrap ga-2">
              <v-btn size="small" variant="tonal" prepend-icon="mdi-content-copy" @click="copyDeepLink">
                {{ $t("ncrm.integrations.telegram.invite.copyLink") }}
              </v-btn>
              <v-btn size="small" variant="text" prepend-icon="mdi-whatsapp" :href="whatsappHref" target="_blank">
                {{ $t("ncrm.integrations.telegram.invite.shareWhatsapp") }}
              </v-btn>
              <v-btn size="small" variant="text" prepend-icon="mdi-email-outline" :href="mailtoHref">
                {{ $t("ncrm.integrations.telegram.invite.shareEmail") }}
              </v-btn>
              <v-spacer />
              <v-btn size="small" variant="text" @click="generate">
                {{ $t("ncrm.integrations.telegram.invite.regenerate") }}
              </v-btn>
            </div>
          </div>

          <v-btn
            v-else-if="!existingLink"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-key-variant"
            :loading="generating"
            :disabled="!selectedAccountId"
            @click="generate"
          >
            {{ $t("ncrm.integrations.telegram.invite.generate") }}
          </v-btn>

          <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">
            {{ errorMessage }}
          </v-alert>
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close">
          {{ $t("ncrm.integrations.telegram.invite.actions.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-snackbar v-model="snackbar.show" :timeout="2500" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
import axios from "axios";
import { formatDisplayDateTime } from "@/Utile/displayFormat.mjs";

export default {
  name: "TelegramInviteContactDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    personId: { type: [Number, String], default: null },
    personName: { type: String, default: "" },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      loading: false,
      generating: false,
      unlinking: false,
      activeBots: [],
      selectedAccountId: null,
      activeCode: null,
      existingLink: null,
      errorMessage: "",
      countdownLabel: "",
      countdownTimer: null,
      snackbar: { show: false, message: "", color: "info" },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit("update:modelValue", v);
      },
    },
    noBots() {
      return this.activeBots.length === 0;
    },
    botOptions() {
      return this.activeBots.map((b) => ({
        title: `${b.name}${b.bot_username ? ` (@${b.bot_username})` : ""}`,
        value: b.id,
      }));
    },
    selectedBotUsername() {
      const bot = this.activeBots.find((b) => b.id === this.selectedAccountId);
      return bot?.bot_username || null;
    },
    deepLink() {
      if (!this.activeCode || !this.selectedBotUsername) return "";
      return `https://t.me/${this.selectedBotUsername}?start=contact_${this.activeCode.code}`;
    },
    inviteMessage() {
      // Mesaj prietenos pe care operatorul îl trimite clientului via WhatsApp/email.
      const greeting = this.personName ? `Salut, ${this.personName}!` : "Salut!";
      return `${greeting} Apasă pe link pentru a primi notificările noastre direct pe Telegram: ${this.deepLink}`;
    },
    whatsappHref() {
      return `https://wa.me/?text=${encodeURIComponent(this.inviteMessage)}`;
    },
    mailtoHref() {
      const subject = encodeURIComponent("Invitație Telegram");
      return `mailto:?subject=${subject}&body=${encodeURIComponent(this.inviteMessage)}`;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.refresh();
      else this.reset();
    },
  },
  beforeUnmount() {
    this.stopCountdown();
  },
  methods: {
    showSnackbar(message, color = "info") {
      this.snackbar = { show: true, message, color };
    },
    formatDate(value) {
      if (!value) return "";
      try {
        return formatDisplayDateTime(value);
      } catch (_) {
        return String(value);
      }
    },
    extractError(error, fallback) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
      );
    },
    reset() {
      this.activeCode = null;
      this.existingLink = null;
      this.errorMessage = "";
      this.stopCountdown();
    },
    async refresh() {
      this.reset();
      this.loading = true;
      try {
        const { data: accountsData } = await axios.get("/integrations/telegram/link/accounts");
        this.activeBots = Array.isArray(accountsData?.accounts) ? accountsData.accounts : [];
        if (!this.selectedAccountId && this.activeBots.length > 0) {
          this.selectedAccountId = this.activeBots[0].id;
        }
        if (!this.selectedAccountId || !this.personId) {
          this.loading = false;
          return;
        }
        // Verifică dacă persoana e deja legată — dacă da, nu mai oferim cod.
        const { data: linkData } = await axios.get("/integrations/telegram/contact-link/by-person", {
          params: { accountId: this.selectedAccountId, personId: this.personId },
        });
        this.existingLink = linkData?.link || null;
        if (!this.existingLink) {
          await this.fetchActiveCode();
        }
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          this.$t("ncrm.integrations.telegram.invite.errors.load")
        );
      } finally {
        this.loading = false;
      }
    },
    async fetchActiveCode() {
      this.activeCode = null;
      this.stopCountdown();
      try {
        const { data, status } = await axios.get("/integrations/telegram/contact-link/code", {
          params: { accountId: this.selectedAccountId, personId: this.personId },
          validateStatus: (s) => s === 200 || s === 204,
        });
        if (status === 200 && data?.code) {
          this.activeCode = { code: data.code, expiresAt: data.expiresAt };
          this.startCountdown();
        }
      } catch (_) {
        // ok if none
      }
    },
    async generate() {
      if (!this.selectedAccountId || !this.personId) return;
      this.generating = true;
      this.errorMessage = "";
      try {
        const { data } = await axios.post("/integrations/telegram/contact-link/code", {
          accountId: this.selectedAccountId,
          personId: this.personId,
        });
        this.activeCode = { code: data.code, expiresAt: data.expires_at };
        this.startCountdown();
        this.showSnackbar(
          this.$t("ncrm.integrations.telegram.invite.codeGenerated"),
          "success"
        );
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          this.$t("ncrm.integrations.telegram.invite.errors.generate")
        );
      } finally {
        this.generating = false;
      }
    },
    startCountdown() {
      this.stopCountdown();
      const tick = () => {
        if (!this.activeCode) return;
        const ms = new Date(this.activeCode.expiresAt).getTime() - Date.now();
        if (ms <= 0) {
          this.activeCode = null;
          this.countdownLabel = "";
          this.stopCountdown();
          return;
        }
        // Pentru coduri de 7 zile arătăm "Xz Yh" sau "Xh Ym" — nu mm:ss ca la
        // codurile employee de 10 min.
        const totalSec = Math.floor(ms / 1000);
        const days = Math.floor(totalSec / 86400);
        const hours = Math.floor((totalSec % 86400) / 3600);
        const minutes = Math.floor((totalSec % 3600) / 60);
        if (days > 0) {
          this.countdownLabel = `${days}z ${hours}h`;
        } else if (hours > 0) {
          this.countdownLabel = `${hours}h ${minutes}m`;
        } else {
          this.countdownLabel = `${minutes}m`;
        }
      };
      tick();
      this.countdownTimer = setInterval(tick, 60_000);
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    async copyDeepLink() {
      try {
        await navigator.clipboard.writeText(this.deepLink);
        this.showSnackbar(this.$t("ncrm.integrations.telegram.invite.copied"), "success");
      } catch (_) {
        this.showSnackbar(this.$t("ncrm.integrations.telegram.invite.errors.copy"), "error");
      }
    },
    async unlink() {
      if (!this.selectedAccountId || !this.personId) return;
      this.unlinking = true;
      try {
        await axios.delete("/integrations/telegram/contact-link", {
          params: { accountId: this.selectedAccountId, personId: this.personId },
        });
        this.showSnackbar(this.$t("ncrm.integrations.telegram.invite.unlinked"), "success");
        await this.refresh();
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          this.$t("ncrm.integrations.telegram.invite.errors.unlink")
        );
      } finally {
        this.unlinking = false;
      }
    },
    close() {
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
.telegram-invite-code-card {
  border: 1px dashed rgba(var(--v-theme-on-surface), 0.18);
  border-radius: 6px;
  padding: 12px;
}
.telegram-invite-code {
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 6px 12px;
  border-radius: 4px;
  background: rgba(var(--v-theme-primary), 0.08);
  color: rgb(var(--v-theme-primary));
  user-select: all;
}
.telegram-invite-deeplink-url {
  font-family: var(--app-font-family-mono);
  font-size: 0.78rem;
  word-break: break-all;
}
</style>
